/**
 * @file ProblemSolvePage.jsx
 * @description The core coding environment for solving DSA problems.
 *              Provides a split-pane layout: problem description on the left,
 *              Monaco-powered code editor on the right, and an animated results
 *              panel that slides up from the bottom.
 *              Handles multi-language templates, run/submit with SSE progress,
 *              cooldown timers, prev/next navigation, and mobile fallback messaging.
 *
 * @module components/ProblemSolvePage
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPost, publicApiGet } from '../utils/apiUtils';
import { FiArrowLeft, FiChevronLeft, FiChevronRight, FiCode, FiBookOpen, FiUnlock, FiUser, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import { motion } from 'framer-motion';
import UserAvatar from './UserAvatar';
import EnhancedCodeEditorPanel from './EnhancedCodeEditorPanel';
import ProblemDescriptionPanel from './ProblemDescriptionPanel';
import EnhancedTestResultsPanel from './EnhancedTestResultsPanel';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api';

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component ProblemSolvePage
 * @description Full-screen coding workspace for a single DSA problem.
 *              Manages code state, language selection, template fetching,
 *              run/submit execution, SSE-streamed grading, and result display.
 *
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @param {string|number} props.questionId - The ID of the current problem.
 *
 * @returns {JSX.Element} The assembled problem-solving view.
 */
const ProblemSolvePage = ({ navigate, questionId }) => {
    const [question, setQuestion] = useState(null);
    const [sampleTestCases, setSampleTestCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [allQuestions, setAllQuestions] = useState([]);
    const [isMobileView, setIsMobileView] = useState(false);
    const [activeTab, setActiveTab] = useState('problem');
    const [showResultsPanel, setShowResultsPanel] = useState(false);
    const [isResultsMinimized, setIsResultsMinimized] = useState(false);
    const [resultsActiveTab, setResultsActiveTab] = useState('tests');
    const [code, setCode] = useState('// Loading template...');
    const [language, setLanguage] = useState(() => localStorage.getItem('preferredLanguage') || null);
    const [languageLoaded, setLanguageLoaded] = useState(false);
    const [originalTemplate, setOriginalTemplate] = useState('');
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [testResults, setTestResults] = useState([]);
    const [submissionResult, setSubmissionResult] = useState(null);
    const [canRun, setCanRun] = useState(true);
    const [canSubmit, setCanSubmit] = useState(true);
    const [runCooldown, setRunCooldown] = useState(0);
    const [submitCooldown, setSubmitCooldown] = useState(0);
    const [isEditorFullscreen, setEditorFullscreen] = useState(false);
    const [submissionProgress, setSubmissionProgress] = useState({ current: 0, total: 0, status: 'idle', passedTests: 0, failedTests: 0, message: '' });

    const languageMap = {
        'javascript': { id: 63, name: 'JavaScript', ext: 'js', logo: 'javascript.png' },
        'python': { id: 71, name: 'Python', ext: 'py', logo: 'python.png' },
        'java': { id: 62, name: 'Java', ext: 'java', logo: 'java.png' },
        'cpp': { id: 52, name: 'C++', ext: 'cpp', logo: 'cpp.png' }
    };

    const { isAuthenticated, user, logout, token } = useAuth();
    const isSubmissionLoading = useRef(false);
    const abortControllerRef = useRef(null);
    const isMountedRef = useRef(true);

    const onReset = () => setCode(originalTemplate);

    // Lock body scroll and clean up abort controllers on unmount
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        isMountedRef.current = true;
        return () => {
            document.body.style.overflow = 'unset';
            isMountedRef.current = false;
            if (abortControllerRef.current) abortControllerRef.current.abort();
        };
    }, []);

    // Dynamic page title: shows question id and title
    useEffect(() => {
        if (question) {
            document.title = `${question.id}. ${question.title} | SimpleCode`;
        }
        return () => { document.title = 'SimpleCode'; };
    }, [question]);

    useEffect(() => {
        const checkMobile = () => setIsMobileView(window.innerWidth < 1024);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const handleError = (event) => {
            const error = event.error || event.reason;
            if (error && (error.name === 'AbortError' || error.type === 'cancelation' || error.msg === 'operation is manually canceled' || (error.message && error.message.includes('canceled')))) {
                event.preventDefault();
                return;
            }
        };
        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleError);
        return () => {
            window.removeEventListener('error', handleError);
            window.removeEventListener('unhandledrejection', handleError);
        };
    }, []);

    useEffect(() => {
        fetchQuestionData();
        fetchAllQuestions();
    }, [questionId, isAuthenticated, token]);

    useEffect(() => {
        if (isSubmissionLoading.current) {
            isSubmissionLoading.current = false;
            return;
        }
        if (language && isAuthenticated) localStorage.setItem('preferredLanguage', language);
        if (language) onReset();
    }, [language]);

    useEffect(() => {
        if (question && language && languageLoaded) fetchCodeTemplate();
    }, [question, language, languageLoaded]);

    useEffect(() => {
        let runTimer, submitTimer;
        if (runCooldown > 0) {
            runTimer = setInterval(() => {
                setRunCooldown(prev => {
                    if (prev <= 1) {
                        setCanRun(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        if (submitCooldown > 0) {
            submitTimer = setInterval(() => {
                setSubmitCooldown(prev => {
                    if (prev <= 1) {
                        setCanSubmit(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            clearInterval(runTimer);
            clearInterval(submitTimer);
        };
    }, [runCooldown, submitCooldown]);

    useEffect(() => {
        setTestResults([]);
        setSubmissionResult(null);
        setSubmissionProgress({ current: 0, total: 0, status: 'idle', passedTests: 0, failedTests: 0, message: '' });
        setShowResultsPanel(false);
        setIsResultsMinimized(false);
        setIsRunning(false);
        setIsSubmitting(false);
        setResultsActiveTab('tests');
    }, [questionId]);

    useEffect(() => {
        if (!languageLoaded) fetchUserPreferredLanguage();
    }, [isAuthenticated, user, languageLoaded]);

    /* ── Data Fetching ── */

    /** Resolves the user's preferred language from last accepted submission or localStorage. */
    const fetchUserPreferredLanguage = async () => {
        const cachedLanguage = localStorage.getItem('preferredLanguage');
        if (!isAuthenticated || !user?.id) {
            if (!language) setLanguage(cachedLanguage || 'cpp');
            setLanguageLoaded(true);
            return;
        }
        if (language) {
            setLanguageLoaded(true);
            try {
                const response = await apiGet(`/auth/submissions/${user.id}?status=Accepted&limit=1`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.submissions && data.submissions.length > 0) {
                        const lastSubmission = data.submissions[0];
                        const languageName = Object.keys(languageMap).find(key => languageMap[key].id === lastSubmission.language_id);
                        if (languageName && languageName !== language) {
                            setLanguage(languageName);
                            localStorage.setItem('preferredLanguage', languageName);
                        }
                    }
                }
            } catch (error) { }
            return;
        }
        try {
            const response = await apiGet(`/auth/submissions/${user.id}?status=Accepted&limit=1`);
            if (response.ok) {
                const data = await response.json();
                if (data.submissions && data.submissions.length > 0) {
                    const lastSubmission = data.submissions[0];
                    const languageName = Object.keys(languageMap).find(key => languageMap[key].id === lastSubmission.language_id);
                    if (languageName) {
                        setLanguage(languageName);
                        localStorage.setItem('preferredLanguage', languageName);
                    } else {
                        setLanguage('cpp');
                    }
                } else {
                    setLanguage('cpp');
                }
            } else {
                setLanguage('cpp');
            }
        } catch (error) {
            setLanguage('cpp');
        } finally {
            setLanguageLoaded(true);
        }
    };

    /** Fetches the full public question list for prev/next navigation. */
    const fetchAllQuestions = async () => {
        try {
            const response = await publicApiGet(`/questions/public`);
            if (!isMountedRef.current) return;
            if (response.ok) {
                const questions = await response.json();
                if (isMountedRef.current) setAllQuestions(questions);
            }
        } catch (err) {
            if (err.name === 'AbortError' || err.type === 'cancelation' || err.msg === 'operation is manually canceled') return;
        }
    };

    /** Fetches the problem data and sample test cases for the current questionId. */
    const fetchQuestionData = async () => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        try {
            setLoading(true);
            setError(null);
            let response;
            if (isAuthenticated) {
                response = await apiGet(`/questions/${questionId}`);
            } else {
                response = await publicApiGet(`/questions/${questionId}/public`);
            }
            if (!isMountedRef.current || abortController.signal.aborted) return;
            if (!response.ok) {
                const errorText = await response.text().catch(() => 'Unknown error');
                let errorMessage;
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.message || `Failed to fetch question: ${response.status}`;
                } catch {
                    errorMessage = `Failed to fetch question: ${response.status} - ${errorText}`;
                }
                throw new Error(errorMessage);
            }
            const questionData = await response.json();
            if (!isMountedRef.current) return;
            setQuestion({ ...questionData.question, constraints: questionData.question.constraints, input_image_path: questionData.question.input_image_path, post_explanation: questionData.question.post_explanation, pre_explanation: questionData.question.pre_explanation });
            setSampleTestCases(questionData.samples || []);
        } catch (err) {
            if (err.name === 'AbortError' || err.type === 'cancelation' || err.msg === 'operation is manually canceled') return;
            if (isMountedRef.current) setError(err.message || err.toString());
        } finally {
            if (isMountedRef.current) setLoading(false);
            if (abortControllerRef.current === abortController) abortControllerRef.current = null;
        }
    };

    /** Fetches the language-specific code template (function signature) for the current problem. */
    const fetchCodeTemplate = async () => {
        try {
            setLoadingTemplate(true);
            const response = await publicApiGet(`/questions/${questionId}/template?language=${language}`);
            if (!isMountedRef.current) return;
            if (response.ok) {
                const template = await response.json();
                if (isMountedRef.current) {
                    setCode(template.function_signature);
                    setOriginalTemplate(template.function_signature);
                }
            } else {
                const fallback = getFallbackTemplate(language);
                if (isMountedRef.current) {
                    setCode(fallback);
                    setOriginalTemplate(fallback);
                }
            }
        } catch (err) {
            if (err.name === 'AbortError' || err.type === 'cancelation' || err.msg === 'operation is manually canceled') return;
            const fallback = getFallbackTemplate(language);
            if (isMountedRef.current) {
                setCode(fallback);
                setOriginalTemplate(fallback);
            }
        } finally {
            if (isMountedRef.current) setLoadingTemplate(false);
        }
    };

    /* ── Navigation Helpers ── */

    /** Returns the index of the current question in the allQuestions array. */
    const getCurrentQuestionIndex = () => allQuestions.findIndex(q => q.id === parseInt(questionId));
    /** Returns the previous question object, or null if at the beginning. */
    const getPrevQuestion = () => {
        const currentIndex = getCurrentQuestionIndex();
        return currentIndex > 0 ? allQuestions[currentIndex - 1] : null;
    };
    /** Returns the next question object, or null if at the end. */
    const getNextQuestion = () => {
        const currentIndex = getCurrentQuestionIndex();
        return currentIndex < allQuestions.length - 1 ? allQuestions[currentIndex + 1] : null;
    };

    /* ── Code Execution ── */

    /** Runs sample test cases against the user's code and displays results. */
    const handleRunTests = async () => {
        if (!isAuthenticated) {
            alert("Please sign in to run your code.");
            return;
        }
        if (!canRun || isRunning || isSubmitting) return;
        setCanRun(false);
        setRunCooldown(5);
        setIsRunning(true);
        setShowResultsPanel(true);
        setIsResultsMinimized(false);
        setResultsActiveTab('tests');
        setTestResults([]);
        try {
            const response = await apiPost('/run-sample', { questionId: parseInt(questionId), sourceCode: code, languageId: languageMap[language].id });
            if (!isMountedRef.current) return;
            if (!response.ok) {
                // Handle rate limit specifically — wire into existing cooldown system
                if (response.status === 429) {
                    const data = await response.json().catch(() => ({}));
                    const retryAfter = data.retryAfter || 60;
                    setRunCooldown(retryAfter);
                    setCanRun(false);
                    // Show it in the results panel instead of throwing
                    setTestResults([{
                        input: 'Rate Limited',
                        expected: 'N/A',
                        actual: 'N/A',
                        passed: false,
                        error: data.message || 'Too many requests. Please wait before running again.'
                    }]);
                    return; // Don't throw — the countdown handles re-enabling the button
                }
                const errorText = await response.text().catch(() => 'Unknown error');
                let errorMessage;
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.message || `Test execution failed: ${response.status}`;
                } catch {
                    errorMessage = `Test execution failed: ${response.status} - ${errorText}`;
                }
                throw new Error(errorMessage);
            }
            const data = await response.json();
            if (!isMountedRef.current) return;
            if (data.results) {
                setTestResults(data.results);
            } else if (data.error) {
                setTestResults([{ input: 'Compilation Error', expected: 'N/A', actual: 'N/A', passed: false, error: data.error }]);
            }
        } catch (err) {
            if (err.name === 'AbortError' || err.type === 'cancelation' || err.msg === 'operation is manually canceled') return;
            if (isMountedRef.current) {
                setTestResults([{ input: 'System Error', expected: 'N/A', actual: 'N/A', passed: false, error: err.message || err.toString() }]);
            }
        } finally {
            if (isMountedRef.current) setIsRunning(false);
        }
    };

    /** Submits the solution for full grading via SSE-streamed progress events. */
    const handleSubmit = async () => {
        if (!isAuthenticated) {
            alert("Please sign in to submit your solution.");
            return;
        }
        if (!canSubmit || isRunning || isSubmitting) return;
        setCanSubmit(false);
        setSubmitCooldown(5);
        setIsSubmitting(true);
        setShowResultsPanel(true);
        setIsResultsMinimized(false);
        setResultsActiveTab('submission');
        setSubmissionResult(null);
        setSubmissionProgress({ current: 0, total: 0, status: 'starting', passedTests: 0, failedTests: 0, message: 'Initializing submission...' });
        try {
            const response = await apiPost('/submit', { userId: user.id, questionId: parseInt(questionId), sourceCode: code, languageId: languageMap[language].id }, { headers: { 'Accept': 'text/event-stream', 'Cache-Control': 'no-cache' } });
            if (!response.ok) {
                if (response.status === 429) {
                    const data = await response.json().catch(() => ({}));
                    const retryAfter = data.retryAfter || 60;
                    setSubmitCooldown(retryAfter);
                    setCanSubmit(false);
                    setSubmissionResult({
                        status: 'Rate Limited',
                        message: data.message || 'Too many submissions. Please wait before submitting again.'
                    });
                    setIsSubmitting(false);
                    return;
                }
                const errorText = await response.text().catch(() => 'Unknown error');
                let errorMessage;
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.message || `Submission failed: ${response.status}`;
                } catch {
                    errorMessage = `Submission failed: ${response.status} - ${errorText}`;
                }
                throw new Error(errorMessage);
            }
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            const processStream = async () => {
                try {
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        if (!isMountedRef.current) break;
                        buffer += decoder.decode(value, { stream: true });
                        let boundary = buffer.indexOf('\n\n');
                        while (boundary !== -1) {
                            const message = buffer.substring(0, boundary);
                            buffer = buffer.substring(boundary + 2);
                            if (message.startsWith('data: ')) {
                                try {
                                    const data = JSON.parse(message.slice(6));
                                    if (isMountedRef.current) handleSSEUpdate(data);
                                } catch (parseError) { }
                            }
                            boundary = buffer.indexOf('\n\n');
                        }
                    }
                } catch (streamError) {
                    if (streamError.name === 'AbortError' || streamError.type === 'cancelation' || streamError.msg === 'operation is manually canceled') return;
                    throw streamError;
                }
            };
            await processStream();
            // Safety net: if the stream ended without a terminal event, force-reset submitting state
            if (isMountedRef.current) {
                setIsSubmitting(false);
            }
        } catch (err) {
            if (err.name === 'AbortError' || err.type === 'cancelation' || err.msg === 'operation is manually canceled') return;
            if (isMountedRef.current) {
                setSubmissionResult({ status: 'Error', message: 'Submission failed: ' + (err.message || err.toString()), failureDetails: null });
                setIsSubmitting(false);
            }
        }
    };

    /** Processes incoming Server-Sent Events during submission grading. */
    const handleSSEUpdate = (data) => {
        switch (data.type) {
            case 'started':
                setSubmissionProgress({ current: 0, total: data.totalTests, status: 'running', passedTests: 0, failedTests: 0, message: `Running ${data.totalTests} test cases...` });
                break;
            case 'test-result':
                setSubmissionProgress(prev => ({ ...prev, current: data.completedCount || data.testIndex, passedTests: data.passedTests || 0, failedTests: data.failedTests || 0, message: `Completed ${data.completedCount || data.testIndex} of ${prev.total} tests...` }));
                break;
            case 'execution-stopped':
                setSubmissionProgress(prev => ({ ...prev, status: 'failed', passedTests: data.passedTests || 0, failedTests: data.failedTests || 0, message: data.message || `Failed at test case ${data.failedAt}` }));
                // Preserve the server's status (Compilation Error / Runtime Error) if present
                setSubmissionResult({ ...data, status: data.status || 'Wrong Answer' });
                setIsSubmitting(false);
                break;
            case 'execution-complete':
                const isAccepted = data.status === 'Accepted';
                setSubmissionProgress(prev => ({ ...prev, current: data.totalTests, status: 'completed', passedTests: data.passedTests || 0, failedTests: data.failedTests || 0, message: isAccepted ? 'All test cases passed!' : data.status }));
                setSubmissionResult({ ...data, message: isAccepted ? 'Solution Accepted! 🎉' : data.status });
                setIsSubmitting(false);
                break;
            case 'system-error':
                setSubmissionProgress(prev => ({ ...prev, status: 'error', message: 'System error occurred' }));
                setSubmissionResult({ ...data, status: 'System Error' });
                setIsSubmitting(false);
                break;
        }
    };

    /** Returns a minimal fallback template when the API template is unavailable. */
    const getFallbackTemplate = (language) => {
        const templates = {
            'javascript': '// Write your solution here\nfunction solution() {\n    // Your code here\n}',
            'python': '# Write your solution here\ndef solution():\n    # Your code here\n    pass',
            'java': '// Write your solution here\nclass Solution {\n    public int solution() {\n        // Your code here\n        return 0;\n    }\n}',
            'cpp': '// Write your solution here\nclass Solution {\npublic:\n    int solution() {\n        // Your code here\n        return 0;\n    }\n};'
        };
        return templates[language] || '// Template not available';
    };

    /** Returns Tailwind text-color class for a given difficulty level. */
    const getDifficultyColor = (difficulty) => {
        switch (difficulty?.toLowerCase()) {
            case 'easy': return 'text-green-600';
            case 'medium': return 'text-yellow-600';
            case 'hard': return 'text-red-600';
            default: return 'text-muted-foreground';
        }
    };

    /** Logs the user out and redirects to the home page. */
    const handleLogout = () => {
        logout();
        navigate('/');
    };

    if (loading || !languageLoaded) {
        return <ProblemSkeleton />;
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex items-center justify-center p-4">
                <div className="text-center bg-accent p-8 rounded shadow-sm border-t-4 border-red-500 max-w-md">
                    <FiAlertCircle className="text-6xl text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-semibold mb-2 text-foreground">Error Loading Problem</h2>
                    <p className="text-muted-foreground mb-6">{error}</p>
                    <div className="flex gap-4 justify-center">
                        <button onClick={fetchQuestionData} className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-semibold transition-all duration-200 flex items-center space-x-2"><FiRefreshCw /><span>Retry</span></button>
                        <button onClick={() => navigate('/dsa-problems')} className="px-6 py-3 border border-border hover:bg-gray-50 text-foreground rounded font-semibold transition-all duration-200 flex items-center space-x-2"><FiArrowLeft /><span>Back</span></button>
                    </div>
                </div>
            </div>
        );
    }

    const prevQuestion = getPrevQuestion();
    const nextQuestion = getNextQuestion();

    return (
        <div className="min-h-screen bg-accent text-foreground" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
            <header className="bg-emerald-900 sticky top-0 z-50">
                <div className="w-full px-3 py-3 relative flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <button onClick={() => navigate('/dsa-problems')} className="flex items-center space-x-2 px-2 py-1 text-gray-300 hover:text-white transition-all duration-200"><FiArrowLeft className="text-lg" /><span className="hidden sm:inline font-medium">Problems</span></button>
                        <div className="flex items-center space-x-1">
                            <button onClick={() => prevQuestion && navigate(`/problems/${prevQuestion.id}`)} disabled={!prevQuestion} className="px-1 h-8 w-8 flex items-center justify-center border border-emerald-700/50 rounded-full text-gray-300 hover:text-white disabled:text-emerald-800 disabled:border-emerald-800/30 disabled:cursor-not-allowed transition-all duration-200" title={prevQuestion ? `Previous: ${prevQuestion.title}` : 'No previous question'}><FiChevronLeft className="text-sm" /></button>
                            <button onClick={() => nextQuestion && navigate(`/problems/${nextQuestion.id}`)} disabled={!nextQuestion} className="px-1 h-8 w-8 flex items-center justify-center border border-emerald-700/50 rounded-full text-gray-300 hover:text-white disabled:text-emerald-800 disabled:border-emerald-800/30 disabled:cursor-not-allowed transition-all duration-200" title={nextQuestion ? `Next: ${nextQuestion.title}` : 'No next question'}><FiChevronRight className="text-sm" /></button>
                        </div>
                    </div>
                    <button onClick={() => navigate('/')} className="absolute left-1/2 -translate-x-1/2"><img src="/logo.png" alt="SimpleCode Desktop" className="h-8 cursor-pointer hover:opacity-80 transition-opacity hidden md:block" /></button>
                    <div className="flex items-center space-x-3">
                        {isAuthenticated ? (
                            <>
                                <button onClick={() => navigate('/profile')} className="flex items-center space-x-2 text-gray-300 hover:text-white">
                                    <UserAvatar avatarUrl={user?.avatar_url} size={24} />
                                    <span className='hidden md:flex capitalize'>{user?.username || 'Profile'}</span>
                                </button>
                                <img src="/logo2.png" alt="SimpleCode Mobile" className="h-4 cursor-pointer hover:opacity-80 transition-opacity block md:hidden" />
                            </>
                        ) : (
                            <img src="/logo2.png" alt="SimpleCode Mobile" className="h-4 cursor-pointer hover:opacity-80 transition-opacity block md:hidden" />
                        )}
                    </div>
                </div>
            </header>

            <div className="flex-1 m-none h-full">
                {!isMobileView && (
                    <div className="flex" style={{ height: showResultsPanel ? (isResultsMinimized ? 'calc(100vh - 104px)' : 'calc(100vh - 50vh - 56px)') : 'calc(100vh - 56px)' }}>
                        <div className="w-1/2 border-r border-gray-300" style={{ height: '100%', maxHeight: '100%' }}><ProblemDescriptionPanel question={question} sampleTestCases={sampleTestCases} isAuthenticated={isAuthenticated} navigate={navigate} /></div>
                        <div className="w-1/2 flex flex-col" style={{ height: '100%', maxHeight: '100%' }}><EnhancedCodeEditorPanel className="code-editor" code={code} setCode={setCode} language={language} setLanguage={setLanguage} languageMap={languageMap} loadingTemplate={loadingTemplate} isRunning={isRunning} isSubmitting={isSubmitting} onRunTests={handleRunTests} onSubmit={handleSubmit} onReset={() => setCode(originalTemplate)} questionId={questionId} isSubmissionLoading={isSubmissionLoading} canRun={canRun} canSubmit={canSubmit} runCooldown={runCooldown} submitCooldown={submitCooldown} navigate={navigate} /></div>
                    </div>
                )}
                {isMobileView && (
                    <div className='overflow-y-auto' style={{
                        height: showResultsPanel
                            ? (isResultsMinimized ? 'calc(100vh - 104px)' : 'calc(100vh - 50vh - 56px)')
                            : 'calc(100vh - 104px)', // Account for header (56px) + new footer (48px)
                        maxHeight: '100vh'
                    }}>
                        <div className="h-full overflow-y-auto">
                            <ProblemDescriptionPanel question={question} sampleTestCases={sampleTestCases} isAuthenticated={isAuthenticated} navigate={navigate} />
                        </div>
                    </div>
                )}
            </div>

            {showResultsPanel && (
                <motion.div
                    initial={false}
                    animate={{ height: isResultsMinimized ? '48px' : '50vh' }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} // Out-Quart curve for smooth deceleration
                    className="fixed bottom-0 left-0 right-0 z-40 overflow-hidden"
                >
                    <EnhancedTestResultsPanel
                        testResults={testResults}
                        submissionResult={submissionResult}
                        submissionProgress={submissionProgress}
                        isRunning={isRunning}
                        isSubmitting={isSubmitting}
                        isMinimized={isResultsMinimized}
                        onMinimize={() => setIsResultsMinimized(!isResultsMinimized)}
                        onClose={() => setShowResultsPanel(false)}
                        activeTab={resultsActiveTab}
                        setActiveTab={setResultsActiveTab}
                    />
                </motion.div>
            )}

            {isMobileView && (
                <div className="fixed bottom-0 left-0 right-0 bg-emerald-800 py-3.5 px-4 z-30 flex items-center justify-center space-x-2.5 text-gray-300 shadow-[0_-4px_20px_rgba(0,0,0,0.04)]">
                    <FiCode className="text-base" />
                    <span className="text-[13px] font-medium tracking-tight">Code Editor is available on Desktop only</span>
                </div>
            )}
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
 * SKELETON LOADER
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component ProblemSkeleton
 * @description Full-page loading placeholder mimicking the split-pane coding layout.
 */
const ProblemSkeleton = () => {
    return (
        <div className="min-h-screen bg-accent text-foreground">
            <header className="bg-emerald-900 border-b border-emerald-800 sticky top-0 z-50">
                <div className="w-full px-3 py-2 flex items-center justify-between animate-pulse">
                    <div className="flex items-center space-x-3">
                        <div className="h-8 w-24 bg-emerald-800 rounded"></div>
                        <div className="flex items-center space-x-1">
                            <div className="h-8 w-8 bg-emerald-800 rounded-full"></div>
                            <div className="h-8 w-8 bg-emerald-800 rounded-full"></div>
                        </div>
                    </div>
                    <div className="h-8 w-32 bg-emerald-800 rounded"></div>
                    <div className="h-8 w-24 bg-emerald-800 rounded"></div>
                </div>
            </header>

            <div className="flex h-[calc(100vh-56px)]">
                <div className="w-1/2 border-r border-gray-300 p-6 space-y-6 animate-pulse">
                    <div className="space-y-3">
                        <div className="h-8 w-3/4 bg-gray-100 rounded"></div>
                        <div className="h-4 w-1/4 bg-gray-100 rounded"></div>
                    </div>
                    <div className="space-y-2">
                        <div className="h-4 w-full bg-gray-100 rounded"></div>
                        <div className="h-4 w-full bg-gray-100 rounded"></div>
                        <div className="h-4 w-3/4 bg-gray-100 rounded"></div>
                    </div>
                    <div className="space-y-4">
                        <div className="h-32 w-full bg-gray-50 rounded"></div>
                        <div className="h-32 w-full bg-gray-50 rounded"></div>
                    </div>
                </div>

                <div className="w-1/2 flex flex-col animate-pulse">
                    <div className="bg-gray-50 border-b border-border px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="h-6 w-24 bg-gray-100 rounded"></div>
                            <div className="flex space-x-2">
                                <div className="h-8 w-16 bg-gray-100 rounded"></div>
                                <div className="h-8 w-20 bg-gray-100 rounded"></div>
                            </div>
                        </div>
                        <div className="h-8 w-32 bg-gray-100 rounded"></div>
                    </div>
                    <div className="flex-1 bg-zinc-900 p-4">
                        <div className="space-y-2">
                            <div className="h-4 w-full bg-zinc-800 rounded"></div>
                            <div className="h-4 w-full bg-zinc-800 rounded"></div>
                            <div className="h-4 w-2/3 bg-zinc-800 rounded"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProblemSolvePage;